import { Box, Button } from "@material-ui/core";
import clsx from "clsx";
import {
  HotelAvailabilityMapTooltip,
  Icon,
  IconName,
  getTotalPriceText,
  genericPriceFormatter,
} from "halifax";
import React, { useContext, useState } from "react";
import {
  CorpLodging,
  getLodgingTrackingProperties,
  HOTEL_LIST_MAP_CAROUSEL,
  IIdLodgings,
  Lodging,
  LodgingId,
  SELECTED_PACKAGE_HOTEL_FROM_MAP,
} from "redmond";
import {
  localCache,
  SHOW_POLICIES_IN_PACKAGES,
  useShowPolicyBanner,
} from "@capone/common";
import { v4 as uuidv4 } from "uuid";
import config from "../../../../../../../../../cap1-application/b2b-base/src/utils/config";
import { ClientContext } from "../../../../../../App";
import { trackEvent } from "../../../../../../api/v0/analytics/trackEvent";
import { ConnectedAvailabilityMapPricePinProps } from "./container";
import "./styles.scss";
import { ISetLodgingIdInFocus } from "../../../../actions/actions";
import { PATH_HOTEL_SHOP } from "../../../../../../utils/paths";
import { transformToStringifiedQuery } from "../../../../../hotel-shop/utils/queryStringHelpers";
import {
  getExperimentVariant,
  CACHE_HOTEL_TOKEN,
  AVAILABLE,
  useExperiments,
} from "../../../../../../context/experiments";

export interface IAvailabilityMapPricePinProps
  extends ConnectedAvailabilityMapPricePinProps {
  lodging: Omit<Lodging, "price">;
  isInFocus: boolean;
  isHovered: boolean;
  isPreviouslyShown: boolean;
  nightCount: number | null;
  isPreview?: boolean;
  isDesktop?: boolean;
  setLodgingIdInFocus: (
    lodgingId: LodgingId | null
  ) => ISetLodgingIdInFocus | void;
}

export const AvailabilityMapPricePin = (
  props: IAvailabilityMapPricePinProps
) => {
  const {
    lodging,
    isInFocus,
    isHovered,
    isPreviouslyShown,
    nightCount,
    setLodgingIdInFocus,
    isPreview,
    isDesktop,
    accountReferenceId,
    packagesByLodgingId,
    travelersCount,
    origin,
    packageHotelQueryParams,
    searchedLocation,
    packagesViewedHotelListProperties,
    searchedHotelId,
  } = props;

  const [hovered, setHovered] = useState(isHovered);
  const { policies } = useContext(ClientContext);
  //   const [showPolicyModal, setShowPolicyModal] = useState<string>("");
  const expState = useExperiments();
  const cacheHotelTokenEnabled =
    getExperimentVariant(expState.experiments, CACHE_HOTEL_TOKEN) === AVAILABLE;

  const tenant = config.TENANT;

  const isCorporate = "corporateTravel" in lodging.lodging;
  //   const outOfPolicyCopy = isAutoApprovalEnabled
  //     ? "If you wish to proceed with your selection, admins will be notified upon booking that this hotel was out of policy."
  //     : "This hotel rate is out of your company policy. You can continue anyway or change your selection.";

  //   const modalType = isAutoApprovalEnabled
  //     ? "out_of_policy_auto"
  //     : "out_of_policy";

  const onClickPricePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInFocus) {
      setLodgingIdInFocus(null);
    } else {
      setLodgingIdInFocus(lodging.lodging.id);
    }
  };

  const isInPolicy =
    isCorporate &&
    (lodging as CorpLodging).lodging.corporateTravel.policyCompliance
      .isInPolicy;

  const hasLoyaltyRates = isCorporate && Boolean(lodging.loyaltyProgramCode);

  React.useEffect(() => {
    setHovered(isHovered);
  }, [isHovered]);

  if (!packagesByLodgingId?.[lodging.lodging.id]?.packageDetails.pricing) {
    return null;
  }

  const price =
    packagesByLodgingId?.[lodging.lodging.id]?.packageDetails.pricing;

  const onCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLodgingIdInFocus(null);
  };

  const onClickHotelDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLodgingIdInFocus(lodging.lodging.id);

    const packagedLodging = packagesByLodgingId?.[lodging.lodging.id];

    const packagePricingDetails = packagedLodging?.packageDetails.pricing;

    const packageDiscount =
      packagePricingDetails?.totalPackageSavings.fiat.value ?? 0;

    const packageDiscountPercentage =
      packageDiscount && packagePricingDetails
        ? (packageDiscount /
            packagePricingDetails.strikethroughTotalPrice.fiat.value) *
          100
        : 0;

    trackEvent({
      eventName: SELECTED_PACKAGE_HOTEL_FROM_MAP,
      properties: {
        ...packagesViewedHotelListProperties.properties,
        ...getLodgingTrackingProperties(lodging).properties,
        is_preferred_cot: lodging.isPreferred,
        package_discount: packageDiscount,
        package_discount_percentage: packageDiscountPercentage,
        is_corporate: lodging.hasCorporateRates,
      },
      encryptedProperties: [
        ...packagesViewedHotelListProperties.encryptedProperties,
        ...getLodgingTrackingProperties(lodging).encryptedProperties,
      ],
    });

    if (origin && packageHotelQueryParams.fromDate !== null) {
      const params = transformToStringifiedQuery({
        lodgingId: lodging.lodging.id,
        origin: origin?.id?.code?.code,
        destination: (searchedLocation?.id as IIdLodgings).lodgingSelection
          .searchTerm,
        placeId: (searchedLocation?.id as IIdLodgings).lodgingSelection.placeId,
        ...packageHotelQueryParams,
        fromDate: packageHotelQueryParams.fromDate,
        shopToken: cacheHotelTokenEnabled
          ? packagesByLodgingId?.[lodging.lodging.id]?.opaqueShopRequest
          : localCache.set(
              uuidv4(),
              packagesByLodgingId?.[lodging.lodging.id]?.opaqueShopRequest ??
                ""
            ).key,
        selectedLodgingIsTopSeller: lodging.highlights?.isTopSeller,
        selectedLodgingMealPlan:
          packagedLodging?.packageDetails.lodgingMealPlan?.kind,
        selectedLodgingIsSearched: searchedHotelId === lodging.lodging.id,
        lodgingSelectedFromMap: true,
      });

      window.open(`${PATH_HOTEL_SHOP}${params}`, "_blank");
    }
  };

  const showPolicyBanner =
    SHOW_POLICIES_IN_PACKAGES && useShowPolicyBanner(policies);

  return (
    <Box
      className={clsx(
        "lodging-marker-wrapper",
        { inFocus: isInFocus },
        { hovered: isInFocus || hovered },
        { previouslyShown: isPreviouslyShown }
      )}
    >
      {isInFocus && isDesktop && (
        <Box
          className={clsx("lodging-details-container", {
            "in-policy": showPolicyBanner && isInPolicy,
          })}
        >
          <HotelAvailabilityMapTooltip
            lodging={lodging}
            onCloseClick={(ev) => onCloseClick(ev)}
            // onClickHotelDetails={() => console.log("Update to go to shop")}
            onClickHotelDetails={(ev) => onClickHotelDetails(ev)}
            nightCount={nightCount || 0}
            rewardsKey={accountReferenceId || undefined}
            // isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
            // isVentureX={isVentureX}
            onViewCarouselImage={() =>
              trackEvent({
                eventName: HOTEL_LIST_MAP_CAROUSEL,
                properties: {},
              })
            }
            policyLimit={policies?.hotels.policies[0].maxPricePerNight}
            tenant={config.TENANT}
            searchedRoomsCount={isCorporate ? 1 : undefined} // [Packages TO-DO]: Update to use actual roomsCount
            // searchedRoomsCount={isCorporate ? roomsCount : undefined}
            // onViewCorpCompareBar={onOpenCompareBarTooltip(
            //   ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_MAP
            // )}
            variant="package"
            packagePricing={
              packagesByLodgingId?.[lodging.lodging.id]?.packageDetails.pricing
            }
            travelersCount={travelersCount}
          />
        </Box>
      )}

      <Button
        className={clsx("price-pin", tenant, {
          "out-of-policy":
            SHOW_POLICIES_IN_PACKAGES && isCorporate && !isInPolicy,
        })}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => (!isPreview ? onClickPricePin(e) : null)}
      >
        {/* {showOnSaleIcon ? (
          <Icon className="preferred-icon" name={IconName.PreferredHotelIcon} />
        ) : null} */}

        {SHOW_POLICIES_IN_PACKAGES && isInPolicy && (
          <Icon className="policy-icon" name={IconName.CheckMarkGreen} />
        )}
        {hasLoyaltyRates && (
          <Icon className="loyalty-icon" name={IconName.StarCircledIcon} />
        )}
        <Box className="price-pin-label">
          {getTotalPriceText({
            price: price.pricePerTraveler.fiat,
            priceFormatter: genericPriceFormatter,
          })}
        </Box>
      </Button>
    </Box>
  );
};
